import * as THREE from 'three';
import { Entity } from './Entity.js';

/**
 * Spark particle effect entity.
 *
 * Spawns when mech parts break off, creating a dramatic visual effect.
 * Sparks are emitted in scattered directions with gravity.
 *
 * Visual:
 * - Orange/yellow sparks
 * - Scatter pattern with more particles than impact
 * - Affected by gravity
 * - Fade out over lifetime
 *
 * Behavior:
 * - Lifetime of 0.4 seconds
 * - 12-16 particles emitted
 * - Automatically destroyed when lifetime expires
 */
export class SparkEffect extends Entity {
  private particles: THREE.Points;
  private lifetime: number = 0.4;
  private velocities: THREE.Vector3[] = [];

  /**
   * Create a new spark effect.
   *
   * @param x - X position in world space
   * @param y - Y position in world space (will be at y=0.5 height)
   */
  constructor(x: number, y: number) {
    super(x, y, 0);
    this.createParticles();
  }

  /**
   * Create the particle system.
   */
  private createParticles(): void {
    const geometry = new THREE.BufferGeometry();
    // More particles than impact effect (12-16 random)
    const count = 12 + Math.floor(Math.random() * 5);
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    // Initialize all particles at the spark point (height 0.5)
    for (let i = 0; i < count; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0.5;
      positions[i * 3 + 2] = 0;

      // Generate random velocity for each particle (wider scatter pattern)
      const angle = Math.random() * Math.PI * 2;
      const speed = 3 + Math.random() * 4; // Faster than impact
      this.velocities.push(
        new THREE.Vector3(
          Math.cos(angle) * speed,
          Math.random() * 3 + 1, // More upward velocity
          Math.sin(angle) * speed
        )
      );

      // Random orange/yellow color for each particle
      const hue = 0.05 + Math.random() * 0.1; // Orange to yellow range
      const color = new THREE.Color().setHSL(hue, 1, 0.5 + Math.random() * 0.3);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2, // Slightly larger than impact particles
      transparent: true,
      vertexColors: true,
    });

    this.particles = new THREE.Points(geometry, material);
    this.mesh = this.particles;

    // Update mesh to match entity position
    this.updateMeshTransform();
  }

  /**
   * Update particle positions and lifetime.
   *
   * @param dt - Fixed delta time in milliseconds
   */
  update(dt: number): void {
    const dtSec = dt / 1000;
    this.lifetime -= dtSec;

    // Destroy when lifetime expires
    if (this.lifetime <= 0) {
      this.destroy();
      return;
    }

    // Update particle positions
    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < this.velocities.length; i++) {
      positions[i * 3] += this.velocities[i].x * dtSec;
      positions[i * 3 + 1] += this.velocities[i].y * dtSec;
      positions[i * 3 + 2] += this.velocities[i].z * dtSec;

      // Apply gravity
      this.velocities[i].y -= 12 * dtSec; // Slightly stronger gravity
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    // Fade out based on remaining lifetime
    const material = this.particles.material as THREE.PointsMaterial;
    material.opacity = this.lifetime / 0.4;
  }
}
